import { Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { FOOTER_ROUTES } from '#features/layout/constants/footerLinks';
import { Link } from '#features/ui';

/**
 * Terms + Privacy line under the sign-in and sign-up forms.
 *
 * These screens are the only thing a signed-out person sees inside the app, and the
 * footer that used to carry the legal links is not rendered there (see AppFooter).
 * Google Play requires the privacy policy to be reachable from within the app, so
 * without this line a guest in the app has no path to it at all.
 *
 * Shape follows what the notice has to do rather than what is easiest to translate:
 * the document names *are* the links, inline in the sentence, and coloured like links.
 * The first version said "our terms" with two grey labels underneath — which reads as
 * plain text next to the Google reCAPTCHA notice right above it, whose links are
 * coloured. A notice only counts if it is conspicuous and names what is being agreed
 * to. It sits next to the buttons it refers to, the social row included: signing in
 * with a provider can create an account, not just sign in.
 *
 * Both documents are bundled with the app (features/static-pages/assets, imported as
 * raw HTML), so the links keep working with no network.
 *
 * The sentence is assembled from separate strings rather than one message with inline
 * tags: this project's .po files carry no tagged messages anywhere, and hand-writing
 * the ICU markers is a good way to break the catalogue silently. Word order is the
 * same in both locales, so nothing is lost, and the two document names reuse strings
 * the footer already ships.
 */
export const AuthLegalNotice = () => {
  const { t } = useLingui();

  return (
    <Text textStyle="caption" color="fg.muted" textAlign="center">
      {t`By continuing you agree to our`}{' '}
      <Link to={FOOTER_ROUTES.terms} color="fg.link">
        {t`Terms of Use`}
      </Link>{' '}
      {t`and`}{' '}
      <Link to={FOOTER_ROUTES.privacy} color="fg.link">
        {t`footer.privacyPolicy`}
      </Link>
      .
    </Text>
  );
};
